/**
 * CnpjValidator - validador de CNPJ compatível com o formato alfanumérico
 * definido pela IN RFB 2.229/2024 (vigência 01/07/2026).
 *
 * Espelha include/cnpj-validator.js do cdl-legado. Mesmo algoritmo, mesmos pesos.
 *
 * Regras:
 *  - 14 posições totais: 12 primeiras podem ser [0-9A-Z]; 2 últimas (DVs) são [0-9].
 *  - Para cada char nos 12 primeiros, valor numérico = charCodeAt(char) - 48
 *    (dig 0..9 -> 0..9; A..Z -> 17..42).
 *  - Pesos DV1: 5,4,3,2,9,8,7,6,5,4,3,2 (12 valores).
 *  - Pesos DV2: 6,5,4,3,2,9,8,7,6,5,4,3,2 (13 valores).
 *  - dv = (soma % 11) < 2 ? 0 : 11 - (soma % 11).
 *
 * CNPJs puramente numéricos legados continuam válidos (mesmo algoritmo,
 * porque charCodeAt('0') - 48 = 0, charCodeAt('1') - 48 = 1, ...).
 */

const LENGTH = 14;
const WEIGHTS_DV1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
const WEIGHTS_DV2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];

/**
 * Remove separadores comuns (. / -) e espaços, força uppercase.
 * NÃO remove letras.
 */
function sanitize(cnpj: string | null | undefined): string {
  if (cnpj === null || cnpj === undefined) {
    return "";
  }
  return String(cnpj)
    .replace(/[^0-9A-Za-z]/g, "")
    .toUpperCase();
}

function computeDv(cnpj: string, length: number, weights: number[]): number {
  let sum = 0;
  for (let i = 0; i < length; i += 1) {
    sum += (cnpj.charCodeAt(i) - 48) * weights[i];
  }
  const rem = sum % 11;
  return rem < 2 ? 0 : 11 - rem;
}

/**
 * Retorna true se cnpj (com ou sem máscara) é válido conforme RFB.
 */
function isValid(cnpj: string | null | undefined): boolean {
  const clean = sanitize(cnpj);
  if (clean.length !== LENGTH) {
    return false;
  }
  if (!/^[0-9A-Z]{12}[0-9]{2}$/.test(clean)) {
    return false;
  }
  if (/^(.)\1{13}$/.test(clean)) {
    return false;
  }
  const dv1 = computeDv(clean, 12, WEIGHTS_DV1);
  if (parseInt(clean.charAt(12), 10) !== dv1) {
    return false;
  }
  const dv2 = computeDv(clean, 13, WEIGHTS_DV2);
  if (parseInt(clean.charAt(13), 10) !== dv2) {
    return false;
  }
  return true;
}

/**
 * Retorna true se cnpj contém pelo menos uma letra (formato alfanumérico RFB).
 */
function isAlphanumeric(cnpj: string | null | undefined): boolean {
  return /[A-Z]/.test(sanitize(cnpj));
}

/**
 * Aplica máscara XX.XXX.XXX/XXXX-XX preservando letras. Se a entrada não
 * tiver exatamente 14 chars válidos após sanitize, retorna o valor original.
 */
function format(cnpj: string | null | undefined): string {
  if (cnpj === null || cnpj === undefined || cnpj === "") {
    return cnpj ?? "";
  }
  const clean = sanitize(cnpj);
  if (clean.length !== LENGTH) {
    return cnpj;
  }
  const parts = [clean.substr(0, 2), clean.substr(2, 3), clean.substr(5, 3)];
  return `${parts.join(".")}/${clean.substr(8, 4)}-${clean.substr(12, 2)}`;
}

const CnpjValidator = {
  isValid,
  isAlphanumeric,
  sanitize,
  format,
  LENGTH
};

export default CnpjValidator;
